import React, { useMemo, useCallback } from 'react';
import { Box, FormControlLabel, Radio, RadioGroup, Tooltip, Typography, Paper } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';

import { QuestionType, type CampaignQuestion, type FormulaQuestion } from '../types';
import { getQuestionDisplayTitle } from '../utils/campaignQuestionsEditorUtils';

const QUALITY_SCORE_NONE = 'none';
const tooltipText =
    'Mark one formula as the primary call quality score. The formula should return a value between 0-100. ' +
    'It will appear in the Calls list with these color indicators: green (good quality) if the score is 60-100%, ' +
    'yellow (neutral) if the score is 50-60%, red (needs attention) if the score is less than 50%.';

const isFormulaQuestion = (q: CampaignQuestion): q is FormulaQuestion => {
    return q.type === QuestionType.FORMULA;
};

/**
 * Component for selecting the quality score formula.
 *
 * @returns {JSX.Element}
 */
const QualityScoreFormulaSelector: React.FC = () => {
    const formContext = useFormContext();

    const allQuestions = (useWatch({
        control: formContext.control,
        name: 'questions',
    }) || []) as CampaignQuestion[];

    // Helper function to update isQualityScore in the form context for a specific question index
    const updateIsQualityScore = useCallback(
        (index: number, value: boolean) => {
            formContext.setValue(`questions.${index}.isQualityScore`, value, { shouldDirty: true });
        },
        [formContext]
    );

    // Filter questions with formulas
    const formulaQuestions = useMemo(() => {
        return allQuestions
            .map((q, index) => ({ question: q, fieldIndex: index }))
            .filter((item) => isFormulaQuestion(item.question) && Boolean(item.question.formula?.trim()));
    }, [allQuestions]);

    const selectedQualityScoreFormulaId = useMemo(() => {
        const index = allQuestions.findIndex((q) => isFormulaQuestion(q) && q.isQualityScore === true);

        if (index === -1) {
            return QUALITY_SCORE_NONE;
        }

        return allQuestions[index].id;
    }, [allQuestions]);

    const handleRadioChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            // Update the isQualityScore values for all formula questions.
            // If the formula is selected, set to true, otherwise set to false (or undefined).
            allQuestions.forEach((q, index) => {
                if (!isFormulaQuestion(q)) return;

                const isSelected = q.id === value;

                updateIsQualityScore(index, isSelected);
            });
        },
        [allQuestions, updateIsQualityScore]
    );

    const radioLabelSx = {
        '& .MuiFormControlLabel-label': { fontSize: '0.875rem', lineHeight: 1.2 },
        mb: -0.5,
    };

    return (
        <Paper sx={{ p: 2, alignSelf: 'flex-start', width: '100%' }} data-testid="quality-score-formula-selector">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.25, mb: 0.5 }}>
                <Typography variant="h5">Call quality scoring</Typography>
                <Tooltip title={tooltipText} placement="top-end" arrow>
                    <InfoOutlined fontSize="small" sx={{ color: 'primary.dark' }} />
                </Tooltip>
            </Box>
            {formulaQuestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Add a formula question to be able to use it as the quality score.
                </Typography>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary">
                        Formula:
                    </Typography>
                    <RadioGroup
                        value={selectedQualityScoreFormulaId}
                        onChange={handleRadioChange}
                        aria-label="Formula for quality score"
                        sx={{ gap: 0 }}
                    >
                        <FormControlLabel value={QUALITY_SCORE_NONE} control={<Radio size="small" />} label="None" sx={radioLabelSx} />
                        {formulaQuestions.map(({ question, fieldIndex }) => (
                            <FormControlLabel
                                key={question.id}
                                value={question.id}
                                control={<Radio size="small" />}
                                label={getQuestionDisplayTitle(question, fieldIndex + 1)}
                                sx={radioLabelSx}
                            />
                        ))}
                    </RadioGroup>
                </>
            )}
        </Paper>
    );
};

export default QualityScoreFormulaSelector;
